import React, { useState } from "react";
import { Sparkles, Send, FlaskConical, BarChart3, Lightbulb, Target, ArrowUp } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import {
  DEMO_ASK_SIM_BANNER,
  DEMO_ASK_INTRO,
  DEMO_ASK_FREE_TEXT_NOTE,
  DEMO_ASK_SECTION_LABELS,
  DEMO_ASK_QUESTIONS,
  matchDemoQuestion,
} from "@/config/demoAskLodge";

const SECTION_STYLE = {
  data: { icon: BarChart3, cls: "border-[#A7F3D0] bg-[#F0FDF4]", tone: "text-[#047857]" },
  analysis: { icon: Lightbulb, cls: "border-[#FDE68A] bg-[#FFFBEB]", tone: "text-[#B45309]" },
  recommendation: { icon: Target, cls: "border-[#BBD8C5] bg-[#F1F7F3]", tone: "text-[#2C5540]" },
};

function DemoAnswer({ item, lang }) {
  const labels = DEMO_ASK_SECTION_LABELS[lang] || DEMO_ASK_SECTION_LABELS.fr;
  const rows = [
    { key: "data", body: item.data[lang] || item.data.fr },
    { key: "analysis", body: item.analysis[lang] || item.analysis.fr },
    { key: "recommendation", body: item.recommendation[lang] || item.recommendation.fr },
  ];
  return (
    <div className="space-y-3" data-testid={`demo-ask-answer-${item.id}`}>
      {rows.map((r) => {
        const meta = SECTION_STYLE[r.key];
        const Icon = meta.icon;
        return (
          <div key={r.key} data-testid={`demo-ask-section-${r.key}`} className={`rounded-xl border p-4 ${meta.cls}`}>
            <div className={`mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest ${meta.tone}`}>
              <Icon size={13} /> {labels[r.key]}
            </div>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#374151]">{r.body}</p>
          </div>
        );
      })}
    </div>
  );
}

// SIMULATED, read-only Ask Lodge for an immutable demo org. No API call, no credit consumption.
// `questions` lets a specific Demo (e.g. Demo Maurice) supply its own guided set; default unchanged.
export default function DemoAskLodge({ questions = DEMO_ASK_QUESTIONS }) {
  const { lang } = useLang();
  const L = lang === "en" ? "en" : "fr";
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [unmatched, setUnmatched] = useState(false);

  const answer = (item) => {
    setUnmatched(false);
    setMessages((m) => [...m, { role: "user", text: item.q[L] }, { role: "assistant", item }]);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const q = input.trim();
    if (!q) return;
    setInput("");
    const match = matchDemoQuestion(q, L, questions);
    if (match) {
      answer(match);
    } else {
      // Never call the API: explain the simulation only supports the guided questions.
      setMessages((m) => [...m, { role: "user", text: q }]);
      setUnmatched(true);
    }
  };

  const empty = messages.length === 0;

  return (
    <div className="flex h-full flex-col" data-testid="ask-lodge-demo">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5" data-testid="ask-lodge-sim-banner">
          <FlaskConical size={16} className="flex-shrink-0 text-amber-600" />
          <span className="text-xs font-semibold text-amber-900">{DEMO_ASK_SIM_BANNER[L]}</span>
        </div>
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#1E3D2B] to-[#2C5540]">
            <Sparkles size={18} className="text-[#5C9873]" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-[#17201A]">Ask Lodge</h1>
            <p className="text-sm text-[#64748B]">{DEMO_ASK_INTRO[L]}</p>
          </div>
        </div>
      </div>

      {/* Conversation */}
      <div className="flex-1 space-y-6 overflow-y-auto pb-4" data-testid="ask-lodge-demo-thread">
        {!empty && (
          <div className="mx-auto max-w-3xl space-y-6">
            {messages.map((m, i) =>
              m.role === "user" ? (
                <div key={i} className="flex justify-end" data-testid={`demo-msg-user-${i}`}>
                  <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-[#1E3D2B] px-4 py-2.5 text-sm font-medium text-white">{m.text}</div>
                </div>
              ) : (
                <div key={i} className="flex items-start gap-3" data-testid={`demo-msg-assistant-${i}`}>
                  <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#1E3D2B] to-[#2C5540]">
                    <Sparkles size={15} className="text-[#5C9873]" />
                  </div>
                  <div className="min-w-0 flex-1"><DemoAnswer item={m.item} lang={L} /></div>
                </div>
              ),
            )}
            {unmatched && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="demo-ask-freetext-note">
                {DEMO_ASK_FREE_TEXT_NOTE[L]}
              </div>
            )}
          </div>
        )}

        {/* Guided questions */}
        <div className="mx-auto max-w-xl">
          <div className="mt-2 flex flex-col gap-2" data-testid="demo-ask-questions">
            {questions.map((item, i) => (
              <button
                key={item.id}
                type="button"
                data-testid={`demo-ask-q-${i}`}
                onClick={() => answer(item)}
                className="group flex items-center justify-between gap-3 rounded-xl border border-[#E8E5DD] bg-white px-4 py-3 text-left text-sm font-medium text-[#374151] transition-colors hover:border-[#5C9873] hover:bg-[#FAFAF6]"
              >
                {item.q[L]}
                <ArrowUp size={15} className="rotate-45 text-slate-300 transition-colors group-hover:text-[#3E7256]" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Input (never calls the API in demo) */}
      <div className="mx-auto w-full max-w-3xl pt-2">
        <form onSubmit={onSubmit} className="flex items-center gap-2 rounded-2xl border border-[#E8E5DD] bg-white p-2 shadow-sm focus-within:border-[#5C9873]">
          <input
            data-testid="demo-ask-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={L === "en" ? "Ask a guided question…" : "Posez une question guidée…"}
            className="flex-1 bg-transparent px-3 py-2 text-sm text-[#17201A] outline-none placeholder:text-slate-400"
          />
          <button
            data-testid="demo-ask-send"
            type="submit"
            disabled={!input.trim()}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#3E7256] text-white transition-colors hover:bg-[#2F5A43] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Send size={16} />
          </button>
        </form>
        <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-[11px] text-slate-400">
          <FlaskConical size={11} /> {DEMO_ASK_SIM_BANNER[L]}
        </p>
      </div>
    </div>
  );
}
